import React, { useState, useEffect, useCallback } from 'react';
import './Dashboard.css';
import Navbar from '../Navbar/Navbar';
import TopBar  from '../Navbar/Mainbar.jsx';
import Swal from 'sweetalert2'; // ✨ Added Swal Import
// Same Add Employee form as the Employee page (adjust the folder name if yours differs)
import EmployeeFormModal, { getApiBase } from '../Employee/EmployeeFormModal';

/* ─── helpers ──────────────────────────────────────────────── */
const formatActivityTime = (ts) => {
  if (!ts) return 'Just now';
  try {
    const date = new Date(ts);
    if (isNaN(date.getTime())) return 'Just now';
    const diffMs = Date.now() - date;
    const m = Math.floor(diffMs / 60000);
    const h = Math.floor(diffMs / 3600000);
    const d = Math.floor(diffMs / 86400000);
    if (m < 1) return 'Just now';
    if (m < 60) return `${m} min${m > 1 ? 's' : ''} ago`;
    if (h < 24) return `${h} hour${h > 1 ? 's' : ''} ago`;
    if (d < 7) return `${d} day${d > 1 ? 's' : ''} ago`;
    return `${String(date.getDate()).padStart(2,'0')}/${String(date.getMonth()+1).padStart(2,'0')}/${date.getFullYear()}`;
  } catch { return 'Just now'; }
};
const formatDateTime = (ts) => {
  if (!ts) return '—';
  const d = new Date(ts);
  if (isNaN(d.getTime())) return '—';
  const date = `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`;
  const time = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  return `${date}, ${time}`;
};
const getTodayString = () =>
  new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

const getInitials = (name) =>
  name ? name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) : 'A';

const dotClass = (type = '') => {
  if (type.includes('Created')) return 'dot-green';
  if (type.includes('Updated')) return 'dot-yellow';
  if (type.includes('Approved')) return 'dot-blue';
  if (type.includes('Bulk')) return 'dot-orange';
  return 'dot-green';
};

const handleYearLimit = (e) => {
  const value = e.target.value;
  if (value) {
    const parts = value.split('-');
    if (parts[0] && parts[0].length > 4) {
      parts[0] = parts[0].slice(0, 4); // Force slice back to 4 digits
      e.target.value = parts.join('-');
    }
  }
};

/* ─── component ────────────────────────────────────────────── */
const AdminDashboard = () => {

  /* modal visibility */
  const [showEmployee,   setShowEmployee]   = useState(false);
  const [showProject,    setShowProject]    = useState(false);
  const [showBulk,       setShowBulk]       = useState(false);
  const [showActivity,   setShowActivity]   = useState(false);
  const [selActivity,    setSelActivity]    = useState(null);

  /* data */
  const [currentUser,    setCurrentUser]    = useState(null);
  const [profilePic,     setProfilePic]     = useState(null);
  const [stats,          setStats]          = useState({ totalConsultants:0, assignedEmployees:0, activeProjects:0, pendingApprovals:0 });
  const [activity,       setActivity]       = useState([]);
  const [employeesList,  setEmployeesList]  = useState([]); 
  const [loading,        setLoading]        = useState(true);
  const [error,          setError]          = useState(null);
  const [submitting,     setSubmitting]     = useState(false);
  const [managerUsers,   setManagerUsers]   = useState([]);
  const [loadingMgr,     setLoadingMgr]     = useState(false);

  /* employee form */

  /* project form */
  const [projForm, setProjForm] = useState({ name:'', code:'', client:'', teamSize:'', manager:'', startDate:'', endDate:'', status:'Active', billable:true, description:'' });
  const [projErr,  setProjErr]  = useState({});

  /* bulk */
  const [bulkType,    setBulkType]    = useState('');
  const [bulkFile,    setBulkFile]    = useState(null);
  const [bulkPreview, setBulkPreview] = useState([]);
  const [bulkResult,  setBulkResult]  = useState(null);


 

  /* ── backend URL ─────────────────────────────────────────── */
  const BACKEND = getApiBase();   // staging site -> staging API, live site -> live API

  /* ── fetch helpers ───────────────────────────────────────── */
  const apiFetch = (path, opts = {}) =>
    fetch(`${BACKEND}${path}`, { credentials: 'include', headers: { 'Accept': 'application/json' }, ...opts });

  const fetchWithTimeout = (path, opts = {}, ms = 8000) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), ms);
    return apiFetch(path, { ...opts, signal: controller.signal })
      .finally(() => clearTimeout(timer));
  };

  /* ── load dashboard ──────────────────────────────────────── */
  useEffect(() => { loadAll(); loadManagers(); }, []);

  const loadManagers = async () => {
    setLoadingMgr(true);
    try {
      const r = await apiFetch('/api/managers-list');
      if (r.ok) {
        const all = await r.json();
        setManagerUsers(all.filter(u => ['Manager', 'Superadmin'].includes(u.role)));
      }
    } catch {}
    finally { setLoadingMgr(false); }
  };

  const refreshStats = async () => {
    try {
      const [empRes, userRes, projRes, pendRes] = await Promise.allSettled([
        fetchWithTimeout('/api/employee-list'),
        fetchWithTimeout('/api/user'),
        fetchWithTimeout('/api/projects'),
        fetchWithTimeout('/api/timesheet/hr/pending-approvals'),
      ]);

      let consultants = 0, assigned = 0;
      let emps = [];
      if (empRes.status === 'fulfilled' && empRes.value.ok) {
        emps = await empRes.value.json();
        setEmployeesList(emps); 
        consultants = emps.filter(e => e.role === 'Consultant').length;
      }

      if (userRes.status === 'fulfilled' && userRes.value.ok) {
        const u = await userRes.value.json();
        setCurrentUser(u);
        assigned = u.role === 'Superadmin'
          ? emps.filter(e => e.role === 'Employee' && e.manager_id !== null).length
          : emps.filter(e => e.role === 'Employee' && e.manager_id === u.id).length;
      }

      let activeProjects = 0;
      if (projRes.status === 'fulfilled' && projRes.value.ok) {
        const projs = await projRes.value.json();
        activeProjects = projs.filter(p => p.status === 'Active').length;
      }

      let pending = 0;
      if (pendRes.status === 'fulfilled' && pendRes.value.ok) {
        const d = await pendRes.value.json();
        pending = (d.submissions || []).filter(s => s.status === 'pending' || s.status === 'edited').length;
      }

      setStats({ totalConsultants: consultants, assignedEmployees: assigned, activeProjects, pendingApprovals: pending });
      await loadActivity();

    } catch {}
  };

  const loadAll = async () => {
    setLoading(true);
    setError(null);
    try {
      await refreshStats();
      fetchProfilePic();
    } catch (e) {
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const loadActivity = async () => {
    try {
      const res = await fetchWithTimeout('/api/dashboard/recent-activity');
      if (!res.ok) { setActivity([]); return; }
      const raw = await res.json();
      const list = Array.isArray(raw) ? raw : (raw?.activities || []);
      const userGeneratedTypes = ['leave_request_submitted'];
      setActivity(
        list
          .filter(a => a && (a.type || a.activity_type || a.activity_title))
          .map(a => {
            const isUserGenerated = userGeneratedTypes.includes(a.activity_type);
            return {
              id:            a.id || Math.random().toString(36).slice(2),
              type:          a.type || a.activity_title || a.activity_type || 'Activity',
              user:          a.user || a.user_name || 'System',
              details:       a.details || 'No details available',
              activity_type: a.activity_type || '',
              created_at:    a.created_at || a.time || new Date().toISOString(),
              email:         (isUserGenerated ? a.user_email : a.target_email) || '—',
              user_email:    a.user_email || '—',   // performer's email
              leave:         a.leave || null,        // full leave detail (leave activities only)
            };
          })
      );
    } catch { setActivity([]); }
  };

  const fetchProfilePic = async () => {
    try {
      const res = await apiFetch('/api/user/profile-picture');
      if (res.ok) { const d = await res.json(); setProfilePic(d.profile_picture); }
    } catch {}
  };

  /* ── project submit ──────────────────────────────────────── */
  const validateProj = () => {
    const e = {};
    if (!projForm.name?.trim())    e.name    = 'Required';
    if (!projForm.code?.trim())    e.code    = 'Required';
    if (!projForm.client?.trim())  e.client  = 'Required';
    if (!projForm.manager)         e.manager = 'Required';
    if (!projForm.startDate)       e.startDate = 'Required';
    if (!projForm.endDate)         e.endDate   = 'Required';
    setProjErr(e);
    return Object.keys(e).length === 0;
  };

  const submitProject = async () => {
    if (!validateProj()) return;
    setSubmitting(true);
    try {
      const res = await apiFetch('/api/projects', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(projForm)
      });
      const r = await res.json();
      if (!res.ok) throw new Error(r.error || 'Failed');

      // ✨ Beautiful Success Swal
     Swal.fire({
         html: `
           <div style="padding: 1rem 0.5rem">
             <div style="width:56px;height:56px;border-radius:50%;background:#f0fdf4;border:1.5px solid #bbf7d0;display:flex;align-items:center;justify-content:center;margin:0 auto 1.25rem">
               <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#16a34a" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                 <polyline points="20 6 9 17 4 12"/>
               </svg>
             </div>
             <p style="font-size:17px;font-weight:600;color:#111;margin:0 0 0.5rem">Project created</p>
             <p style="font-size:13px;color:#6b7280;line-height:1.6;margin:0">The project was created successfully.</p>
           </div>
         `,
         width: '340px',
         confirmButtonText: 'OK',
         confirmButtonColor: '#111111',
         customClass: {
          popup: 'swal-employee rounded-2xl',
          confirmButton: 'rounded-sm text-sm font-medium px-6',
         }
       });
     
     } catch (e) {
       Swal.fire({
         html: `
           <div style="padding: 1rem 0.5rem">
             <div style="width:56px;height:56px;border-radius:50%;background:#fef2f2;border:1.5px solid #fecaca;display:flex;align-items:center;justify-content:center;margin:0 auto 1.25rem">
               <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#ef4444" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                 <line x1="18" y1="6" x2="6" y2="18"/>
                 <line x1="6" y1="6" x2="18" y2="18"/>
               </svg>
             </div>
             <p style="font-size:17px;font-weight:600;color:#111;margin:0 0 0.5rem">Something went wrong</p>
             <p style="font-size:13px;color:#6b7280;line-height:1.6;margin:0">${e.message || 'Something went wrong while saving the project.'}</p>
           </div>
         `,
         width: '340px',
         confirmButtonText: 'Try again',
          confirmButtonColor: '#111111',
         customClass: {
           popup: 'swal-employee rounded-2xl',
           confirmButton: 'rounded-sm text-sm font-medium px-6',
         }
       });
     }
    finally { setSubmitting(false); }
  };

  /* ── bulk import ─────────────────────────────────────────── */
  const downloadTemplate = () => {
    if (!bulkType) { 
      Swal.fire({
        title: 'Selection Required',
        text: 'Please choose an import type selection filter first.',
        icon: 'warning',
        iconColor: '#F59E0B',
        width: '360px',
        confirmButtonColor: '#374151',
        customClass: { popup: 'rounded-2xl shadow-xl' }
      });
      return; 
    }
    const csv = bulkType === 'employees'
      ? 'Name,Email,Phone Number,Role,Department,Manager Email,Location\nJohn Doe,john@company.com,9876543210,Employee,Engineering,mgr@company.com,"City, Country"\n'
      : 'Project Name,Project Code,Client,Team Size,HR Name,Start Date,End Date,Status,Billable,Description\nSample,PRJ-001,Client,5,HR,2025-01-01,2025-12-31,Active,Yes,Desc\n';
    const a = Object.assign(document.createElement('a'), {
      href: URL.createObjectURL(new Blob([csv], { type: 'text/csv' })),
      download: bulkType === 'employees' ? 'employee_template.csv' : 'project_template.csv'
    });
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.endsWith('.csv')) { 
      Swal.fire({
        title: 'Invalid Format',
        text: 'Please upload a clean structured layout .CSV dataset layout array file.',
        icon: 'error',
        iconColor: '#EF4444',
        width: '360px',
        confirmButtonColor: '#374151',
        customClass: { popup: 'rounded-2xl shadow-xl' }
      });
      return; 
    }
    if (file.size > 10 * 1024 * 1024) { 
      Swal.fire({
        title: 'File Too Large',
        text: 'The absolute maximum target file metric size ceiling limits cap is set to 10MB.',
        icon: 'warning',
        iconColor: '#F59E0B',
        width: '360px',
        confirmButtonColor: '#374151',
        customClass: { popup: 'rounded-2xl shadow-xl' }
      });
      return; 
    }
    setBulkFile(file);
    setBulkResult(null);
    const reader = new FileReader();
    reader.onload = ({ target: { result } }) => {
      const lines = result.split('\n').filter(l => l.trim());
      const headers = lines[0].split(',').map(h => h.trim());
      setBulkPreview(lines.slice(1, 6).map(line => {
        const vals = line.split(',').map(v => v.trim().replace(/^"|"$/g,''));
        return headers.reduce((o, h, i) => ({ ...o, [h]: vals[i] || '' }), {});
      }));
    };
    reader.readAsText(file);
  };

  const submitBulk = async () => {
    if (!bulkFile || !bulkType) { 
      Swal.fire({
        title: 'Action Denied',
        text: 'Select an operations type schema structure context and assign a file pack.',
        icon: 'warning',
        iconColor: '#F59E0B',
        width: '360px',
        confirmButtonColor: '#374151',
        customClass: { popup: 'rounded-2xl shadow-xl' }
      });
      return; 
    }
    setSubmitting(true);
    setBulkResult(null);
    const reader = new FileReader();
    reader.onload = async ({ target: { result } }) => {
      const lines = result.split('\n').filter(l => l.trim());
      let ok = 0, fail = 0, errs = [];
      for (let i = 1; i < lines.length; i++) {
        const vals = lines[i].split(',').map(v => v.trim().replace(/^"|"$/g, ''));
        try {
          const [endpoint, data] = bulkType === 'employees'
            ? ['/api/create-employee', { name:vals[0], email:vals[1], ContactNumber1:vals[2], role:vals[3], department:vals[4], manager_id:null, location:vals[6], status:'active' }]
            : ['/api/projects',        { name:vals[0], code:vals[1], client:vals[2], teamSize:vals[3], manager:vals[4], startDate:vals[5], endDate:vals[6], status:vals[7]||'Active', billable:vals[8]?.toLowerCase()==='yes', description:vals[9]||'' }];
          const res = await apiFetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify(data)
          });
          if (res.ok) { ok++; }
          else { fail++; const errData = await res.json(); errs.push(`Row ${i}: ${errData.error || 'Failed'}`); }
        } catch (err) { fail++; errs.push(`Row ${i}: ${err.message}`); }
      }
      setBulkResult({ ok, fail, errs });
      setSubmitting(false);
      refreshStats(); 
    };
    reader.readAsText(bulkFile);
  };

  if (loading) return (
    <div className="adash-root">
      <div className="adash-sidebar"><Navbar /></div>
      <div className="adash-main" style={{ display:'flex', alignItems:'center', justifyContent:'center' }}>
        <p style={{ color:'#6b7280' }}>Loading dashboard…</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="adash-root">
      <div className="adash-sidebar"><Navbar /></div>
      <div className="adash-main" style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:16 }}>
        <p style={{ color:'#ef4444' }}>{error}</p>
        <button onClick={loadAll} style={{ padding:'10px 24px', borderRadius:8, background:'#3b82f6', color:'#fff', border:'none', cursor:'pointer', fontWeight:600 }}>Retry</button>
      </div>
    </div>
  );

  return (
    <div className="adash-root">
      <div className="adash-sidebar"><Navbar /></div>
      <div className="adash-main">
        <TopBar currentUser={currentUser} profilePic={profilePic} />

        <div className="adash-body">
          <div className="adash-banner">
            <div className="adash-banner-left">
              <h1>Welcome back, {currentUser?.name || 'Admin'}!</h1>
              <div className="adash-banner-badge">
                <svg width="13" height="13" fill="none" viewBox="0 0 24 24">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {currentUser?.role || 'Admin'} · {currentUser?.email || ''}
              </div>
            </div>
            <div className="adash-banner-right">
              <div className="adash-today-label">Today</div>
              <div className="adash-today-date">{getTodayString()}</div>
            </div>
          </div>

          <div className="adash-content">
            <div className="adash-stats">
              {[
                { label:'Total Consultant',    cls:'',     img:'/Images/Totalemp.png',     val:stats.totalConsultants,   sub:'+2 this month',   subCls:'' },
                { label:'Assigned Employees',  cls:'',     img:'/Images/Totalemp.png',     val:stats.assignedEmployees,   sub:'+0 this month',   subCls:'' },
                { label:'Active Projects',     cls:'teal', img:'/Images/Activeproject.png',val:stats.activeProjects,     sub: stats.activeProjects > 0 ? `${Math.floor(stats.activeProjects*0.75)} billable` : 'No projects', subCls:'neutral' },
                { label:'Project Pendings',    cls:'red',  img:'/Images/clock-red.png',    val:stats.pendingApprovals,   sub: stats.pendingApprovals > 0 ? 'Needs attention' : 'All clear', subCls:'neutral' },
              ].map(({ label, cls, img, val, sub, subCls }) => (
                <div className="adash-stat-card" key={label}>
                  <div className="adash-stat-head">
                    <span className={`adash-stat-label ${cls}`}>{label}</span>
                    <div className="adash-stat-icon"><img src={img} alt="" /></div>
                  </div>
                  <div className="adash-stat-value">{val}</div>
                  <div className={`adash-stat-sub ${subCls}`}>{sub}</div>
                </div>
              ))}
            </div>

            <div className="adash-actions">
              <button className="adash-action-btn adash-btn-blue"   onClick={() => setShowEmployee(true)}>
                <img src="/Images/Addemp.png" alt="" />Add Employee
              </button>
              <button className="adash-action-btn adash-btn-pink"   onClick={() => setShowProject(true)}>
                <img src="/Images/Add-project.png" alt="" />Add Project
              </button>
              <button className="adash-action-btn adash-btn-orange" onClick={() => setShowBulk(true)}>
                <img src="/Images/Bulk-import.png" alt="" />Bulk Import
              </button>
              <button className="adash-action-btn adash-btn-green">
                <img src="/Images/Leave-policies.png" alt="" />Leave Policies
              </button>
            </div>

            <div className="adash-activity-card">
              <h3>Recent System Activity</h3>
              <div className="adash-activity-list">
                {activity.length > 0 ? activity.map((a, i) => (
                  <div className="adash-activity-row" key={i}>
                    <div className={`adash-activity-dot ${dotClass(a.activity_type || a.type)}`} />
                    <div className="adash-activity-info">
                      <div className="adash-activity-title">
                        {a.leave?.leave_type?.toLowerCase() === 'permission'
                          ? a.type.replace(/leave/i, 'Permission')
                          : a.type}
                      </div>
                      <div className="adash-activity-user">
                        {a.leave
                          ? <><span style={{ color:'#1d4ed8' }}>{a.leave.employee_name}</span></>
                          : a.user}
                      </div>
                    </div>
                    <div className="adash-view-btn" onClick={() => { setSelActivity(a); setShowActivity(true); }}>VIEW</div>
                  </div>
                )) : (
                  <div className="adash-empty">No recent activity</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {showActivity && selActivity && (() => {
       
        const performerEmail = (selActivity.user_email || selActivity.email || '').trim().toLowerCase();
        const employeeEmail  = (selActivity.leave?.employee_email || '').trim().toLowerCase();
        const onBehalf = !!selActivity.leave
          && employeeEmail && employeeEmail !== '—'
          && performerEmail && performerEmail !== '—'
          && employeeEmail !== performerEmail;

        return (
        <div className="adash-overlay">
          <div style={{ background:'#fff', borderRadius:18, width:'100%', maxWidth:600, overflow:'hidden', boxShadow:'0 8px 40px rgba(0,0,0,0.18)' }}>
            <div style={{ background:'linear-gradient(135deg,#1e3a6e,#1d4ed8)', padding:'20px 24px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                <div style={{ width:38, height:38, borderRadius:'50%', background:'rgba(255,255,255,0.15)', border:'1.5px solid rgba(255,255,255,0.3)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                  <svg width="17" height="17" fill="none" viewBox="0 0 24 24" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                  </svg>
                </div>
                <span style={{ color:'#fff', fontSize:15, fontWeight:700 }}>Activity Details</span>
              </div>
              <button onClick={() => setShowActivity(false)} style={{ width:28, height:28, borderRadius:'50%', background:'rgba(255,255,255,0.15)', border:'none', color:'#fff', fontSize:18, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>×</button>
            </div>
            <div style={{ padding:'20px 24px', display:'grid', gridTemplateColumns:'1fr 1fr', columnGap:16, alignItems:'start' }}>
              {[
                // { label:'Activity Type', value: selActivity.type || '—', full:false },
                // { label: onBehalf ? 'Filed By' : 'Performed By', value: selActivity.user || '—', full:false },
                // { label:'Email Address', value: selActivity.user_email || selActivity.email || '—', full:true, blue:true },
              ].map(({ label, value, full, blue }) => (
                <div key={label} style={{ gridColumn: full ? '1/-1' : 'auto', marginBottom:12 }}>
                  <div style={{ fontSize:'10.5px', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.07em', color:'#9ca3af', marginBottom:4 }}>{label}</div>
                  <div style={{ background:'#f8fafc', border:'1px solid #e2e8f0', borderRadius:8, padding:'9px 12px' }}>
                    <span style={{ fontSize:'13.5px', color: blue ? '#1d4ed8' : '#111827', fontWeight:500, wordBreak:'break-all' }}>{value}</span>
                  </div>
                </div>
              ))}

             
              {/* {onBehalf && (
                <div style={{ gridColumn:'1/-1', marginBottom:14, display:'flex', alignItems:'center', gap:8, background:'#eff6ff', border:'1px solid #bfdbfe', borderRadius:8, padding:'9px 12px' }}>
                  <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink:0 }}>
                    <path d="M17 21v-2a4 4 0 0 0-3-3.87"/><path d="M7 21v-2a4 4 0 0 1 3-3.87"/><circle cx="12" cy="7" r="4"/>
                  </svg>
                  <span style={{ fontSize:'12.5px', color:'#1e40af', fontWeight:500, lineHeight:1.45 }}>
                    Filed by <b>{selActivity.user}</b> on behalf of <b>{selActivity.leave.employee_name}</b>
                  </span>
                </div>
              )} */}

              {selActivity.leave && (
                <>
                  <div style={{ height:1, background:'#f1f5f9', margin:'4px 0 14px' }} />
                  <div style={{ fontSize:'11px', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.07em', color:'#6b7280', marginBottom:10 }}></div>
                  {[
                    { label:'Employee',   value: selActivity.leave.employee_email && selActivity.leave.employee_email !== '—' ? `${selActivity.leave.employee_email}` : ''},
                    { label:'Leave Type', value: (() => {
                        const type = selActivity.leave.leave_type || '';
                        if (type.toLowerCase() === 'permission') {
                          const hrs = selActivity.leave.hours
                                  ?? selActivity.leave.permission_hours
                                  ?? selActivity.leave.duration_hours;
                          return hrs != null ? `${type}  ·  ${hrs} hour(s)` : type;
                        }
                        return `${type}${selActivity.leave.days != null ? `  ·  ${selActivity.leave.days} day(s)` : ''}`;
                      })()
                    },
                    { label:'Leave Dates', value: selActivity.leave.start_date === selActivity.leave.end_date ? selActivity.leave.start_date : `${selActivity.leave.start_date}  →  ${selActivity.leave.end_date}` },
                    { label:'Reason',     value: selActivity.leave.reason || 'No reason provided' },
                    { label:'Applied On', value: formatDateTime(selActivity.leave.applied_at) },
                    { label:'Status',     value: selActivity.leave.status },
                    { label:'Approved By', value: selActivity.leave.approver_name || 'Not yet approved' },
                    { label:'Approved On', value: selActivity.leave.approved_at ? formatDateTime(selActivity.leave.approved_at) : '—' },
                  ].map(({ label, value }) => (
                    <div key={label} style={{ marginBottom:10 }}>
                      <div style={{ fontSize:'10.5px', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.07em', color:'#9ca3af', marginBottom:4 }}>{label}</div>
                      <div style={{ background:'#f8fafc', border:'1px solid #e2e8f0', borderRadius:8, padding:'9px 12px' }}>
                        <span style={{ fontSize:'13.5px', color:'#111827', fontWeight:500, wordBreak:'break-word' }}>{value}</span>
                      </div>
                    </div>
                  ))}
                </>
              )}

            
              {!selActivity.leave && (
                <>
              <div style={{ height:1, background:'#f1f5f9', margin:'4px 0 14px' }} />
                  <div style={{ gridColumn:'1/-1' }}>
                <div style={{ fontSize:'10.5px', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.07em', color:'#9ca3af', marginBottom:4 }}>Details</div>
                <div style={{ background:'#f8fafc', border:'1px solid #e2e8f0', borderRadius:8, padding:'10px 14px', fontSize:13, color:'#374151', lineHeight:1.55 }}>
                  {selActivity.details || 'No additional details.'}
                </div>
              </div>
                </>
              )}
            </div>
            <div style={{ padding:'14px 24px 18px', display:'flex', alignItems:'center', justifyContent:'space-between', background:'#f8fafc', borderTop:'1px solid #f1f5f9' }}>
              <span style={{ fontSize:12, color:'#9ca3af' }}>{formatActivityTime(selActivity.created_at)}</span>
              <button onClick={() => setShowActivity(false)} style={{ background:'#1d4ed8', color:'#fff', border:'none', borderRadius:8, padding:'8px 22px', fontSize:13, fontWeight:600, cursor:'pointer' }}>Close</button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Add Employee - the same form as the Employee page */}
      <EmployeeFormModal
        open={showEmployee}
        employees={employeesList}
        onClose={() => setShowEmployee(false)}
        onSaved={() => refreshStats()}
      />

      {showProject && (
        <div className="adash-overlay">
          <div className="adash-modal">
            <div className="adash-modal-head">
              <h2>Add New Project</h2>
              <button className="adash-modal-close" onClick={() => {
                setShowProject(false);
                setProjForm({ name:'', code:'', client:'', teamSize:'', manager:'', startDate:'', endDate:'', status:'Active', billable:true, description:'' });
                setProjErr({});
              }}>×</button>
            </div>
            <div className="adash-modal-body">
              {[
                { label:'Project Name', key:'name', ph:'Enter project name' },
                { label:'Project Code', key:'code', ph:'e.g., AXB-LQS-2025' },
                { label:'Client Name',  key:'client', ph:'Enter client name' },
              ].map(({ label, key, ph }) => (
                <div className="adash-fg" key={key}>
                  <label className="adash-label">{label} <span className="adash-req">*</span></label>
                  <input className="adash-input" placeholder={ph} value={projForm[key]} onChange={e => setProjForm(p => ({...p, [key]: e.target.value}))} />
                  {projErr[key] && <span className="adash-err">{projErr[key]}</span>}
                </div>
              ))}
              {[["Start Date","startDate"],["End Date","endDate"]].map(([label,key]) => (
                <div className="adash-fg" key={key}>
                  <label className="adash-label">{label} <span className="adash-req">*</span></label>
                  <input className="adash-input" type="date" max="9999-12-31" onInput={handleYearLimit} value={projForm[key]} onChange={e => setProjForm(p => ({...p, [key]: e.target.value}))} />
                  {projErr[key] && <span className="adash-err">{projErr[key]}</span>}
                </div>
              ))}
              <div className="adash-fg">
                <label className="adash-label">Team Size</label>
                <input className="adash-input" placeholder="e.g., 5 members" value={projForm.teamSize} onChange={e => setProjForm(p => ({...p, teamSize: e.target.value}))} />
              </div>
              <div className="adash-fg">
                <label className="adash-label">Project Manager <span className="adash-req">*</span></label>
                <select className="adash-select" value={projForm.manager} onChange={e => setProjForm(p => ({...p, manager: e.target.value}))}>
                  <option value="">Select Manager</option>
                  {managerUsers.map(h => <option key={h.id} value={h.name}>{h.name} ({h.email})</option>)}
                </select>
                {managerUsers.length === 0 && <span className="adash-hint">No users with Manager role available.</span>}
                {projErr.manager && <span className="adash-err">{projErr.manager}</span>}
              </div>
              <div className="adash-fg">
                <label className="adash-label">Status <span className="adash-req">*</span></label>
                <select className="adash-select" value={projForm.status} onChange={e => setProjForm(p => ({...p, status: e.target.value}))}>
                  <option>Active</option><option>Completed</option><option>On Hold</option>
                </select>
              </div>
              <div className="adash-fg">
                <label className="adash-label">Description</label>
                <textarea className="adash-textarea" rows={4} placeholder="Project description…" value={projForm.description} onChange={e => setProjForm(p => ({...p, description: e.target.value}))} />
              </div>
            </div>
            <div className="adash-modal-foot">
              <button className="adash-btn cancel" onClick={() => {
                setShowProject(false);
                setProjForm({ name:'', code:'', client:'', teamSize:'', manager:'', startDate:'', endDate:'', status:'Active', billable:true, description:'' });
                setProjErr({});
              }} disabled={submitting}>Cancel</button>
              <button className="adash-btn submit" onClick={submitProject} disabled={submitting}>
                {submitting ? 'Adding…' : 'Add Project'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showBulk && (
        <div className="adash-overlay">
          <div className="adash-modal sm">
            <div className="adash-modal-head">
              <h2>Bulk Import</h2>
              <button className="adash-modal-close" onClick={() => {
                setShowBulk(false);
                setBulkType(''); setBulkFile(null); setBulkPreview([]); setBulkResult(null);
              }}>×</button>
            </div>
{/* employee previous leave balance is 5. current year jan to may employees el balance is 6
june month he took 1 leave
how much leave balance is left for employee
 */}
            <div className="adash-modal-body">
              <div className="adash-instructions">
                <div style={{ fontSize:18, marginBottom:6 }}>⚠️</div>
                <div className="adash-instructions-title">Important Instructions:</div>
                <ul>
                  <li>Select import type (Employees or Projects)</li>
                  <li>Download the CSV template</li>
                  <li>Fill in the data according to the template</li>
                  <li>Upload the completed CSV file</li>
                  <li>Review preview and confirm import</li>
                </ul>
              </div>

              <div className="adash-fg">
                <label className="adash-label">Import Type <span className="adash-req">*</span></label>
                <p style={{ margin: '0 0 6px', fontSize: 12, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 4 }}>
                  ℹ️ Select type before downloading template or uploading file.
                </p>
                <select className="adash-select" value={bulkType} onChange={e => {
                  setBulkType(e.target.value);
                  setBulkFile(null); setBulkPreview([]); setBulkResult(null);
                }}>
                  <option value="">Select type</option>
                  <option value="employees">Employees</option>
                  <option value="projects">Projects</option>
                </select>
              </div>

              <button className="adash-download-btn" onClick={downloadTemplate} disabled={!bulkType}>
                <svg width="16" height="16" fill="none" viewBox="0 0 16 16">
                  <path d="M8 11V3M5 8l3 3 3-3M2 13h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Download CSV Template
              </button>

              <div className="adash-fg">
                <label className="adash-label">Upload File</label>
                <div className="adash-upload-area">
                  <input type="file" accept=".csv" id="bulk-file" style={{ display:'none' }} onChange={handleFileUpload} />
                  <label htmlFor="bulk-file" style={{ cursor:'pointer', display:'block' }}>
                    <svg width="30" height="30" fill="none" viewBox="0 0 32 32">
                      <path d="M16 20V8M12 12l4-4 4 4M6 24h20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <p className="adash-upload-text">{bulkFile ? bulkFile.name : 'Click to upload or drag and drop'}</p>
                    <p className="adash-upload-hint">CSV file (max 10MB)</p>
                  </label>
                </div>
              </div>

               {bulkResult && (
                <div style={{
                  marginTop: 14,
                  borderRadius: 10,
                  overflow: 'hidden',
                  border: `1px solid ${bulkResult.fail === 0 ? '#bbf7d0' : '#fecaca'}`
                }}>
                  <div style={{
                    background: bulkResult.fail === 0 ? '#f0fdf4' : '#fef2f2',
                    padding: '10px 14px',
                    display: 'flex', alignItems: 'center', gap: 8
                  }}>
                    <span style={{ fontSize: 18 }}>{bulkResult.fail === 0 ? '✅' : '⚠️'}</span>
                    <span style={{ fontWeight: 700, fontSize: 13, color: bulkResult.fail === 0 ? '#15803d' : '#dc2626' }}>
                      Import {bulkResult.fail === 0 ? 'Completed Successfully' : 'Completed with Errors'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', borderTop: '1px solid #e5e7eb' }}>
                    <div style={{ flex: 1, padding: '10px 14px', textAlign: 'center', borderRight: '1px solid #e5e7eb' }}>
                      <div style={{ fontSize: 22, fontWeight: 800, color: '#16a34a' }}>{bulkResult.ok}</div>
                      <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>Successful</div>
                    </div>
                    <div style={{ flex: 1, padding: '10px 14px', textAlign: 'center' }}>
                      <div style={{ fontSize: 22, fontWeight: 800, color: bulkResult.fail > 0 ? '#dc2626' : '#9ca3af' }}>{bulkResult.fail}</div>
                      <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>Failed</div>
                    </div>
                  </div>
                  {bulkResult.errs.length > 0 && (
                    <div style={{ borderTop: '1px solid #fecaca', background: '#fff5f5', padding: '8px 14px', maxHeight: 100, overflow: 'auto' }}>
                      {bulkResult.errs.slice(0, 5).map((errMsg, i) => (
                        <div key={i} style={{ fontSize: 12, color: '#dc2626', padding: '2px 0' }}>• {errMsg}</div>
                      ))}
                      {bulkResult.errs.length > 5 && (
                        <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
                          +{bulkResult.errs.length - 5} more errors…
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {bulkPreview.length > 0 && (
                <div style={{ marginTop:14 }}>
                  <div style={{ fontSize:13, fontWeight:700, color:'#374151', marginBottom:8 }}>Preview (first 5 rows):</div>
                  <div style={{ maxHeight:160, overflow:'auto', border:'1px solid #e5e7eb', borderRadius:10, padding:10, background:'#f9fafb' }}>
                    {bulkPreview.map((row, i) => (
                      <div key={i} style={{ fontSize:12, padding:6, borderBottom: i < bulkPreview.length-1 ? '1px solid #e5e7eb' : 'none' }}>
                        <b>Row {i+1}:</b> {JSON.stringify(row)}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="adash-modal-foot">
              <button className="adash-btn cancel" onClick={() => {
                setShowBulk(false);
                setBulkType(''); setBulkFile(null); setBulkPreview([]); setBulkResult(null);
              }} disabled={submitting}>Cancel</button>
              <button className="adash-btn submit" onClick={submitBulk} disabled={!bulkFile || !bulkType || submitting}>
                {submitting ? 'Importing…' : 'Import Data'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;